import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { cloneDeep as _cloneDeep, isEmpty, isEqual as _isEqual } from 'lodash';
import { Observable } from 'rxjs/Observable';
import { ISelectable } from '../../interfaces/selectable.interface';
import { TitleItem } from '../../classes/title-configuration.class';
import { DynamicFormItem } from '@ispm/dynamic-form/classes/dynamic-form-item.class';
import { ConditionalItem } from '@ispm/shared/classes/conditional-item.class';
import { TherapiesActionCreatorService } from '@ispm/therapies/services/therapies-action-creator.service';
import { IDiagnosticOrder } from '@ispm/test/interfaces/diagnostic-order.interface';
import { DiagnosticOrderActionsService } from '@ispm/test/services/diagnostic-order-actions.service';
import { Subject } from 'rxjs/Subject';
import { Workflow } from '@ispm/core/interfaces/workflow.interface';
import {ActivatedRoute} from "@angular/router";
import { filter, map, take, takeUntil } from "rxjs/operators";
import { updateClinicalRecommendationActionCreator } from "@ispm/therapies/store/therapies.actions";
import { Store } from '@ngrx/store';

@Component({
  selector: 'gen-expandable-information-card',
  templateUrl: './expandable-information-card.component.html',
  styleUrls: ['./expandable-information-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExpandableInformationCardComponent implements ISelectable, OnInit, OnDestroy {
  public headerCss: any;
  public isVertical: boolean;

  @Input()
  public testId: string;

  @Input()
  public cssClass: string;

  @Input()
  public set editable(e: boolean) {
    if (e !== undefined) {
      this._editable = e;
    }
  }

  public get editable() {
    return this._editable;
  }

  @Input()
  public set data(data: any) {
    if (_isEqual(this.data, data)) {
      return;
    }

    if (!isEmpty(data)) {
      this._data = _cloneDeep(data);
      this._originalData = _cloneDeep(data);
      this.init();

      if (this._initialLoad) {
        this._initialLoad = false;
      } else {
        this._titles = !isEmpty(this._originalTitles) ?this. _originalTitles.map(title => new TitleItem(title.text, title.link, this.data)) : [];
        this.updateForm();
      }
      this._changeDetector.markForCheck();
    }
  }

  public get data() {
    return this._data;
  }

  @Input()
  public additionalData: {
    resourceMap?: Array<{
      conditions: [],
      layoutForms: { headerFormId: string, bodyFormId: string }
    }>,
    layoutForms?: { headerFormId: string, bodyFormId: string }
  };

  @Input()
  public formId: string;

  @Input()
  public formConfig: DynamicFormItem[];

  @Input()
  public selectByDefault: false;

  @Input()
  public set titles(titles: Array<TitleItem>) {
    this._originalTitles = _cloneDeep(titles);
    this._titles = !isEmpty(titles) ? titles.map(title => new TitleItem(title.text, title.link, this.data)) : [];
  }

  public get titles() {
    return this._titles;
  }

  @Input()
  indexProperty: string = 'id';

  @Input()
  isSelectable: boolean;

  @Output()
  onDeselect: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  onSelect: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  editSave: EventEmitter<any> = new EventEmitter();

  @Output()
  editCanceled: EventEmitter<any> = new EventEmitter();

  public headerFormId: string;
  public bodyFormId: string;
  public cardEditMode = true;
  public disabled = true;
  public saveButtonsVisible = false;
  public collapsedPanel = true;
  public resetForm = false;
  public submitted = false;
  public isSelected$: Observable<boolean>;

  private _data: any;
  private _originalData: any;
  private _editable: boolean = true;
  private _titles: TitleItem[];
  private _originalTitles: TitleItem[];
  private _initialLoad: boolean = true;
  private _destroyed$ = new Subject();
  private _revisionId: string;

  constructor(
    private _ngZone: NgZone,
    private _changeDetector: ChangeDetectorRef,
    private _therapiesActionCreator: TherapiesActionCreatorService,
    private _diagnosticOrderActionsService: DiagnosticOrderActionsService,
    private activatedRoute: ActivatedRoute,
    private _store: Store,
  ) {
  }

  ngOnInit(): void {
    this.init();
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  private init(): void {
    this._revisionId = this.activatedRoute.parent.snapshot.params.revisionId;

    if (!this.additionalData) {
      return;
    }

    this.isSelected$ = this._therapiesActionCreator.getIsSelectedStream(
      TherapiesActionCreatorService.getTherapyIdentifier(this.data, this.indexProperty)
    );

    if (this.additionalData.layoutForms) {
      this.headerFormId = this.additionalData.layoutForms.headerFormId;
      this.bodyFormId = this.additionalData.layoutForms.bodyFormId;
    } else if (Array.isArray(this.additionalData.resourceMap)) {
      const forms = this.additionalData.resourceMap.find(resourceMap => {
        return resourceMap.conditions.every(condition => {
          const buildCondition = ConditionalItem.from(this._data, condition);
          return buildCondition.verify();
        });
      });

      if (forms) {
        this.headerFormId = forms.layoutForms.headerFormId;
        this.bodyFormId = forms.layoutForms.bodyFormId;
      }
    }

    this._diagnosticOrderActionsService.getState()
      .pipe(
        takeUntil(this._destroyed$.asObservable()),
        filter(state => !!state.order && Object.keys(state.order).length > 0),
        take(1),
        map(state => state.order)
      )
      .subscribe((current: IDiagnosticOrder) => {
        const currentRevision = current.workflowRequest.revisions.find(rev => rev.id === this._revisionId);

        if (Workflow.checkCurrentStatus(currentRevision, ['test completed', 'stopped'])) {
          this._editable = false;
        }
      });
  }

  toggleEdit() {
    this.disabled = !this.disabled;

    if (this.collapsedPanel) {
      this.collapsedPanel = false;
    }

    if (!this.disabled) {
      this.saveButtonsVisible = true;
    } else {
      this.cancelUpdate();
    }
  }

  toggleCollapsed() {
    this.collapsedPanel = !this.collapsedPanel;
  }

  updateContent() {
    this.editSave.emit(this.data);
    this.submitted = true;
    this._therapiesActionCreator.updateClinicalRecommendation(this.data, this.testId)
      .pipe(take(1))
      .subscribe(updatedContent => {
        this._store.dispatch(updateClinicalRecommendationActionCreator(updatedContent))
        this.data = updatedContent;
        this._originalData = updatedContent;
        this.toggleEdit();
        this._changeDetector.markForCheck();
        this._changeDetector.detectChanges();
      }
    );
  }

  cancelUpdate() {
    // Call the setter
    this.data = this._originalData;
    this.editCanceled.emit(this.data);
    this.saveButtonsVisible = false;
    this.disabled = true;
    this.updateForm();
  }

  updateForm() {
    this._ngZone.runOutsideAngular(() => setTimeout(() => {
      this.resetForm = true;
      setTimeout(() => this.resetForm = false, 1000);
    }, 1000));
  }

  selectDrug(status: boolean) {
    status
      ? this.onSelect.emit(this.data)
      : this.onDeselect.emit(this.data)
    ;
  }
}
